import { useMemo } from 'react';
import {
  AuthApi,
  OrganizationsApi,
  IdPConnectionsApi,
  IdPMappingRulesApi,
  DashboardsApi,
  SignalsApi,
  SecretsApi,
  CredentialsApi,
  CredentialStoresApi,
  CredentialAssignmentsApi,
  MetricsApi,
  RolesApi,
  APIKeysApi,
  AuditLogApi,
  IntegrationsApi,
  CloudIntegrationsApi,
  SitesApi,
  ConnectorClustersApi,
  ConnectorsApi,
  DevicesApi,
  CMDBReconciliationApi,
  VisibilityApi,
  TopologyApi,
  MembershipsApi,
  InvitationsApi,
  DiscoveryApi,
  InventoryApi,
  ChangeValidationsApi,
  ChatRunsApi
} from '@app/types/sdk';
import type {
  AuthApiClass,
  OrganizationsApiClass,
  IdPConnectionsApiClass,
  IdPMappingRulesApiClass,
  DashboardsApiClass,
  SignalsApiClass,
  SecretsApiClass,
  CredentialsApiClass,
  CredentialStoresApiClass,
  CredentialAssignmentsApiClass,
  MetricsApiClass,
  RolesApiClass,
  APIKeysApiClass,
  AuditLogApiClass,
  IntegrationsApiClass,
  CloudIntegrationsApiClass,
  SitesApiClass,
  ConnectorClustersApiClass,
  ConnectorsApiClass,
  DevicesApiClass,
  CMDBReconciliationApiClass,
  VisibilityApiClass,
  TopologyApiClass,
  MembershipsApiClass,
  InvitationsApiClass,
  DiscoveryApiClass,
  InventoryApiClass,
  ChangeValidationsApiClass,
  ChatRunsApiClass
} from '@app/types/sdk';
import { useApiConfiguration } from '@app/hooks/useApiConfiguration';

const withTopologyPathCompat = (api: InstanceType<typeof TopologyApi>): TopologyApiClass => {
  const compatApi = api as TopologyApiClass;

  if (typeof compatApi.searchTopologyPathEndpoints !== 'function') {
    compatApi.searchTopologyPathEndpoints = async () => {
      throw new Error('Topology path endpoint search is unavailable in the current generated TopologyApi client.');
    };
  }

  if (typeof compatApi.traceTopologyPath !== 'function') {
    compatApi.traceTopologyPath = async () => {
      throw new Error('Topology path tracing is unavailable in the current generated TopologyApi client.');
    };
  }

  return compatApi;
};

export const useAuthApi = (): AuthApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new AuthApi(configuration), [configuration]);
};

export const useInventoryApi = (): InventoryApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new InventoryApi(configuration), [configuration]);
};

export const useChangeValidationsApi = (): ChangeValidationsApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new ChangeValidationsApi(configuration), [configuration]);
};

export const useChatRunsApi = (): ChatRunsApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new ChatRunsApi(configuration), [configuration]);
};

export const useOrganizationsApi = (): OrganizationsApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new OrganizationsApi(configuration), [configuration]);
};

export const useIdpConnectionsApi = (): IdPConnectionsApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new IdPConnectionsApi(configuration), [configuration]);
};

export const useIdpMappingRulesApi = (): IdPMappingRulesApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new IdPMappingRulesApi(configuration), [configuration]);
};

export const useDashboardsApi = (): DashboardsApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new DashboardsApi(configuration), [configuration]);
};

export const useSignalsApi = (): SignalsApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new SignalsApi(configuration), [configuration]);
};

export const useSecretsApi = (): SecretsApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new SecretsApi(configuration), [configuration]);
};

export const useCredentialsApi = (): CredentialsApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new CredentialsApi(configuration), [configuration]);
};

export const useCredentialStoresApi = (): CredentialStoresApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new CredentialStoresApi(configuration), [configuration]);
};

export const useCredentialAssignmentsApi = (): CredentialAssignmentsApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new CredentialAssignmentsApi(configuration), [configuration]);
};

export const useMetricsApi = (): MetricsApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new MetricsApi(configuration), [configuration]);
};

export const useRolesApi = (): RolesApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new RolesApi(configuration), [configuration]);
};

export const useApiKeysApi = (): APIKeysApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new APIKeysApi(configuration), [configuration]);
};

export const useAuditLogApi = (): AuditLogApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new AuditLogApi(configuration), [configuration]);
};

export const useIntegrationsApi = (): IntegrationsApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new IntegrationsApi(configuration), [configuration]);
};

export const useCloudIntegrationsApi = (): CloudIntegrationsApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new CloudIntegrationsApi(configuration), [configuration]);
};

export const useSitesApi = (): SitesApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new SitesApi(configuration), [configuration]);
};

export const useConnectorClustersApi = (): ConnectorClustersApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new ConnectorClustersApi(configuration), [configuration]);
};

export const useConnectorsApi = (): ConnectorsApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new ConnectorsApi(configuration), [configuration]);
};

export const useDevicesApi = (): DevicesApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new DevicesApi(configuration), [configuration]);
};

export const useCMDBReconciliationApi = (): CMDBReconciliationApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new CMDBReconciliationApi(configuration), [configuration]);
};

export const useVisibilityApi = (): VisibilityApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new VisibilityApi(configuration), [configuration]);
};

export const useTopologyApi = (): TopologyApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => withTopologyPathCompat(new TopologyApi(configuration)), [configuration]);
};

export const useMembershipsApi = (): MembershipsApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new MembershipsApi(configuration), [configuration]);
};

export const useInvitationsApi = (): InvitationsApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new InvitationsApi(configuration), [configuration]);
};

export const useDiscoveryApi = (): DiscoveryApiClass => {
  const configuration = useApiConfiguration();
  return useMemo(() => new DiscoveryApi(configuration), [configuration]);
};
